import type { FC, ReactNode } from 'react';
import { useEffect, useCallback } from 'react';
import { configureAxios, setRefreshTokenCallback } from '@/shared/lib/axios';
import { useAuthStore } from '@/shared/store/auth/useAuthStore';
import { useToast } from '@/shared/ui/toast';
import { logger } from '@/shared/utils/logger';
import { useAuthContext } from './AuthProvider';

interface AxiosAuthProviderProps {
  children: ReactNode;
}

export const AxiosAuthProvider: FC<AxiosAuthProviderProps> = ({ children }) => {
  const { accessToken, isAuthenticated, getNewToken, logout } = useAuthContext();
  const { showWarning, showError } = useToast();

  // Create stable refresh callback for axios interceptor
  const refreshCallback = useCallback(async (): Promise<string | null> => {
    logger.debug('Axios interceptor requesting token refresh');
    return getNewToken({ forceRefresh: true });
  }, [getNewToken]);

  // Register refresh callback with axios module
  useEffect(() => {
    setRefreshTokenCallback(refreshCallback);
    return () => setRefreshTokenCallback(null);
  }, [refreshCallback]);

  // Mobile browsers often suspend tabs long enough for the access token to
  // expire. Refresh opportunistically when the app becomes active again so the
  // first staff dashboard request after idle time does not have to recover from
  // a 401.
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshOnResume = () => {
      if (document.visibilityState === 'hidden') return;

      void getNewToken({ forceRefresh: false });
    };

    window.addEventListener('focus', refreshOnResume);
    document.addEventListener('visibilitychange', refreshOnResume);

    return () => {
      window.removeEventListener('focus', refreshOnResume);
      document.removeEventListener('visibilitychange', refreshOnResume);
    };
  }, [getNewToken, isAuthenticated]);

  // Listen for session expired events (triggered by axios on 401 after refresh failure)
  useEffect(() => {
    const handleSessionExpired = () => {
      // Check if we're in a checkout flow - don't logout during Stripe return
      // checkoutSessionId is set before Stripe redirect and persisted to localStorage
      const { checkoutSessionId } = useAuthStore.getState();
      if (checkoutSessionId) {
        logger.debug('Ignoring session-expired during checkout flow - Auth0 still initializing');
        return; // Don't logout - Auth0 is just still loading after Stripe redirect
      }

      logger.warn('Session expired event received - logging out');
      showWarning('Your session has expired. Please log in again.');
      logout();
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () =>
      window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [logout, showWarning]);

  // Listen for permission denied events (triggered by axios on 403)
  useEffect(() => {
    const handlePermissionDenied = (event: Event) => {
      const customEvent = event as CustomEvent<{
        url?: string;
        permission?: string;
        message?: string;
      }>;
      const { message } = customEvent.detail;

      showError(message || "You don't have permission to perform this action");
    };

    window.addEventListener(
      'auth:permission-denied',
      handlePermissionDenied as EventListener,
    );
    return () =>
      window.removeEventListener(
        'auth:permission-denied',
        handlePermissionDenied as EventListener,
      );
  }, [showError]);

  // Configure axios with auth token
  useEffect(() => {
    // Configure axios with the cached access token from AuthProvider
    // This prevents calling getAccessTokenSilently on every API request
    if (isAuthenticated && accessToken) {
      configureAxios(accessToken);
    }
  }, [isAuthenticated, accessToken]);

  return children;
};
